/**
 * 图片「占位 + 淡入」：还没加载完的图先不显示（露出父容器背景），加载完再淡入。
 *
 * 为什么不用「给每个 <img> 写 @load」：全项目 80 多处 img，逐个改既漏又啰嗦，
 * 而且新写的组件很容易忘。这里用全局 MutationObserver 兜住所有图片 —— 包括
 * v-for 渲染出来的、切 tab 才插进来的、el-image 内部的 img。
 *
 * 具体做法：
 *   1. 图片插入 DOM 时判断它是否已加载完（缓存命中时 complete 为 true）；
 *   2. 没加载完的打个 data-img-pending，CSS 里把它 opacity 设 0；
 *   3. load / error 任一发生就摘掉这个标记，CSS 的 transition 负责淡入。
 *
 * 两个刻意的取舍：
 *   - 「已缓存」的图不加 pending，避免首屏已就绪的图反而闪一下；
 *   - 只处理「新插入」的节点，同一个 img 换 src 时不重复加 pending ——
 *     这时浏览器会保留旧图直到新图加载完，比闪成空白更好看。
 */
const PENDING_ATTR = 'data-img-pending'

/** 给单张图片挂上监听（幂等，同一元素只处理一次） */
function watchImage(img) {
  if (!img || img.__imgFadeWatched) return
  img.__imgFadeWatched = true

  // 已经加载好的（缓存命中、或者服务端直出的静态图）直接显示
  if (img.complete && img.naturalWidth > 0) return

  img.setAttribute(PENDING_ATTR, '1')

  const done = () => {
    img.removeAttribute(PENDING_ATTR)
    img.removeEventListener('load', done)
    img.removeEventListener('error', done)
  }
  img.addEventListener('load', done)
  img.addEventListener('error', done)
}

/** 处理一批新增节点里的图片（包括节点自身就是 img、以及它的后代） */
function scanNode(node) {
  if (!node || node.nodeType !== 1) return
  if (node.tagName === 'IMG') {
    watchImage(node)
    return
  }
  if (typeof node.querySelectorAll === 'function') {
    const imgs = node.querySelectorAll('img')
    for (let i = 0; i < imgs.length; i++) watchImage(imgs[i])
  }
}

/**
 * 安装全局监听。在 Vue 挂载之后调用一次即可。
 * @returns {MutationObserver} 观察器（一般不需要手动断开；如需清理可调用 disconnect）
 */
export function installImageFade() {
  if (typeof document === 'undefined') return null

  // 首屏已有的图片
  const existing = document.querySelectorAll('img')
  for (let i = 0; i < existing.length; i++) watchImage(existing[i])

  if (typeof MutationObserver === 'undefined') return null

  const observer = new MutationObserver((records) => {
    for (let r = 0; r < records.length; r++) {
      const added = records[r].addedNodes
      for (let i = 0; i < added.length; i++) scanNode(added[i])
    }
  })

  observer.observe(document.body, { childList: true, subtree: true })
  return observer
}

export default installImageFade
